import { createSSGApp } from '@quickwire/qssg';
import { createPinia } from 'pinia';
import { routes } from 'vue-router/auto-routes';
import { env } from '@/scripts/env';
import { getImage } from '@/scripts/images';
import App from './App.vue';
import '@/styles/main.css';

// Static route → banner image key map. Dynamic `:id` claim routes are matched
// by prefix below in bannerForPath(). Admin pages (k12/college-admin) are SPA-only
// so they never get an SSG preload — the guard handles them too.
const ROUTE_BANNERS: Record<string, string> = {
  '/': 'home',
  '/k12': 'k12',
  '/k12/admin': 'k12-admin',
  '/k12/admin/login': 'k12-admin',
  '/k12/claim': 'k12-form',
  '/k12/claim/info': 'providers',
  '/college': 'college',
  '/college/admin': 'college-admin',
  '/college/admin/login': 'college-admin',
  '/college/claim': 'providers',
  '/college/claim/info': 'providers',
  '/aboutus': 'about',
  '/aboutus/allproducts': 'products',
  '/aboutus/faq': 'faq',
  '/aboutus/requestinfo': 'request-info',
  '/aboutus/requestinfo/thankyou': 'request-info',
  '/contactus': 'contact',
  '/contactus/thankyou': 'contact',
  '/privacy': 'privacy',
  '/providers': 'providers',
  '/specialrisk': 'providers',
};

function bannerForPath(path: string): string | undefined {
  if (ROUTE_BANNERS[path]) return ROUTE_BANNERS[path];
  if (path.startsWith('/k12/claim/')) return 'k12-form';
  if (path.startsWith('/college/claim/')) return 'providers';
  return undefined;
}

/**
 * `@quickwire/qssg` entry. Pre-renders the static routes listed in the router at build time and
 * genuinely hydrates that markup at runtime. Dynamic `:id` routes are never pre-rendered; they are
 * served the `spa.html` shell and render client-side.
 *
 * **This really does hydrate now, and vite-ssg did not.** vite-ssg's `hydration` option defaults to
 * `false` and we never passed client options, so its client entry called plain
 * `createApp().mount()` — and Vue's mount path clears `container.textContent` before rendering.
 * Every page load threw away the pre-rendered DOM and re-rendered from scratch; the static HTML was
 * an SEO/first-paint artifact only. `createSSGApp` uses `createSSRApp` in a production build, so
 * the markup is now adopted rather than discarded. Consequence to keep in mind when touching
 * anything that renders differently on server and client: an SSR/client divergence that was
 * previously invisible is now a hydration mismatch. Never seed reactive state at construction from
 * something the pre-render cannot see — `localStorage`, `Date`, `window`. `stores/reference.ts`
 * did, and it cost one mismatch per page load for returning visitors. The same applies to anything
 * that races mount: the setup callback below runs _before_ `app.mount()`, so work started there can
 * land in reactive state ahead of hydration. Both halves of the reference bootstrap now run from
 * `App.vue`'s `onMounted` via `initAfterHydration()`, which is the only thing that should touch the
 * store before a user interaction. The `spa.html` fallback is not exposed: it ships an empty
 * `#app`, and Vue's hydrate path bails to a full client mount on an empty container.
 *
 * No manual `createHead()` install: `createSSGApp` owns the head, creating a fresh instance per
 * render from `@unhead/vue/server` (pre-render) or `@unhead/vue/client` (browser) and installing it
 * on the app. Do not add one here, and do not add `@unhead/vue/vite` to `vite.config.ts > plugins`
 * either — `ssgPlugin` already includes it. Either mistake creates a second head registry and
 * silently drops every `useHead({ ... })` call from SSG output, observed as every pre-rendered page
 * sharing the index.html default title/description and AppImage's `<link rel=preload>` never
 * reaching `<head>`.
 */
export const createApp = createSSGApp(
  App,
  {
    routes,
    scrollBehavior(
      to: { hash?: string },
      _from: unknown,
      savedPosition: { left?: number; top?: number; behavior?: ScrollBehavior } | null,
    ) {
      if (savedPosition) return savedPosition;
      if (to.hash) return { el: to.hash };
      return { top: 0 };
    },
  },
  ({ app, router, isClient }) => {
    // Touch validated env so the module-load check runs at app boot and tree-shakers
    // don't drop the import.
    void env;
    const pinia = createPinia();
    app.use(pinia);

    if (isClient) {
      // Nothing touches the reference store here. This callback runs before `app.mount()` — qssg
      // awaits `router.isReady()` in between — so anything landing in reactive state inside that
      // gap makes the first client render disagree with the pre-rendered HTML.
      // `App.vue`'s `onMounted` owns the bootstrap instead; see `initAfterHydration()`.

      // Preload the destination banner AVIF before the incoming component mounts.
      // On full page loads qssg bakes <link rel="preload"> into the static HTML
      // so the browser discovers the image at parse time. On SPA navigation the
      // pre-rendered HTML for the destination is never fetched, so useHead() in
      // AppImage fires after mount — too late to front-run the request. This guard
      // fires before the route resolves, closing that discovery gap.
      // This guard owns one <link> and updates it in place across navigations rather than
      // appending a new node each time. On a hard load of a banner page the document already
      // carries AppImage's pre-rendered preload as well, so head holds two — same imagesrcset,
      // so the browser issues one request. The guard deliberately does not adopt that node:
      // unhead owns it and may replace it on navigation, which would leave this reference
      // detached and silently stop updating the preload.
      // The srcset goes on before the first append: a preload link enters the document with no
      // `href`, so attached without `imagesrcset` it has nothing to fetch and Chrome logs
      // "<link rel=preload> has an invalid `href` value".
      let bannerPreload: HTMLLinkElement | null = null;
      router.beforeEach((to: { path: string }) => {
        const key = bannerForPath(to.path);
        if (!key) return;
        const link = bannerPreload ?? document.createElement('link');
        link.setAttribute('imagesrcset', getImage(key).avif);
        link.setAttribute('imagesizes', '100vw');
        if (!bannerPreload) {
          link.rel = 'preload';
          link.as = 'image';
          link.type = 'image/avif';
          link.setAttribute('fetchpriority', 'high');
          document.head.appendChild(link);
          bannerPreload = link;
        }
      });
    }
  },
);
